import type { ComboboxOption } from "@v1/components/designSystem/combobox/ComboboxContext";
import type { DynamicDataTargetType } from "replo-runtime/shared/dynamicData";
import type {
  ReploShopifyVariant,
  ShopifySellingPlan,
  StoreProduct,
} from "replo-runtime/shared/types";
import type { Context } from "replo-runtime/store/ReploVariable";
import type {
  Component,
  CustomComponentPropType,
  CustomPropDefinition,
} from "schemas/component";
import type {
  ProductRef,
  SelectedSellingPlanIdOrOneTimePurchase,
} from "schemas/product";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import ButtonGroupComponent from "@v1/components/common/designSystem/ButtonGroup";
import DeprecatedLabeledControl from "@v1/components/common/designSystem/DeprecatedLabeledControl";
import DynamicDataButton from "@v1/components/common/designSystem/DynamicDataButton";
import Input from "@v1/components/common/designSystem/Input";
import FormFieldXButton from "@v1/components/common/FormFieldXButton";
import { Badge } from "@v1/components/designSystem/badge/Badge";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import Selectable from "@v1/components/designSystem/Selectable";
import SwitchWithDescription from "@v1/components/designSystem/switch/SwitchWithDescription";
import { DeprecatedTextarea } from "@v1/components/designSystem/textarea/Textarea";
import { CodeEditorCustomPropModifier } from "@v1/components/editor/customProp/CodeEditorCustomPropModifier";
import { FieldWithDescription } from "@v1/components/editor/customProp/FieldWithDescription";
import { ImageCustomPropModifier } from "@v1/components/editor/customProp/ImageCustomPropModifier";
import { OptionsCustomPropModifier } from "@v1/components/editor/customProp/OptionsCustomPropModifier";
import { ProductsCustomPropModifier } from "@v1/components/editor/customProp/ProductsCustomPropModifier";
import { SwatchesCustomPropModifier } from "@v1/components/editor/customProp/SwatchesCustomPropModifier";
import DynamicDataValueIndicator from "@v1/components/editor/page/element-editor/components/extras/DynamicDataValueIndicator";
import {
  CombinedItemsSelector,
  ItemsSelector,
} from "@v1/components/editor/page/element-editor/components/modifiers/ItemsModifier";
import { LengthInputSelector } from "@v1/components/editor/page/element-editor/components/modifiers/LengthInputModifier";
import Stepper from "@v1/components/editor/page/element-editor/components/modifiers/Stepper";
import { useOverridableInput } from "@v1/hooks/useOverridableInput";
import { selectStoreShopifyUrl } from "@v1/reducers/core-reducer";
import { selectThemeId } from "@v1/reducers/ui-reducer";
import { useEditorSelector } from "@v1/store/store";
import { getPathFromVariable } from "@v1/utils/dynamic-data";
import { ConnectShopifyCallout } from "@v2/components/ConnectShopifyCallout";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { assetRouter } from "@v2/trpc/routes/asset";
import isBoolean from "lodash-es/isBoolean";
import orderBy from "lodash-es/orderBy";
import { Minus, PackagePlus, Plus } from "lucide-react";
import {
  DEFAULT_ACTIVE_CURRENCY,
  DEFAULT_ACTIVE_LANGUAGE,
  DEFAULT_MONEY_FORMAT,
  DEFAULT_MONEY_WITH_CURRENCY_FORMAT,
} from "replo-runtime/shared/liquid";
import { isDynamicDataValue } from "replo-runtime/shared/utils/dynamic-data";
import { getProduct } from "replo-runtime/store/ReploProduct";
import { isNotNullish } from "replo-utils/lib/misc";
import { useDebouncedCallback } from "replo-utils/react/use-debounced-callback";
import { ReploError } from "schemas/errors";

import SellingPlanSelector from "./actions/SellingPlanSelector";
import { IntegerSelector } from "./customProp/IntegerSelector";
import { DynamicDataSelector } from "./page/DynamicDataSelector";
import SolidColorSelector from "./page/element-editor/components/SolidColorSelector";

class InvalidDateValueError extends ReploError {
  constructor(value: string) {
    super({
      message: "Invalid value returned from date picker input",
      additionalData: { value },
    });
  }
}

// https://webreflection.medium.com/using-the-input-datetime-local-9503e7efdce
function toDatetimeLocal(date: Date) {
  const ten = function (i: number) {
      return (i < 10 ? "0" : "") + i;
    },
    YYYY = date.getFullYear(),
    MM = ten(date.getMonth() + 1),
    DD = ten(date.getDate()),
    HH = ten(date.getHours()),
    II = ten(date.getMinutes()),
    SS = ten(date.getSeconds());
  return `${YYYY}-${MM}-${DD}T${HH}:${II}:${SS}`;
}

type CustomComponentPropRenderProps = {
  component: Component;
  value: any;
  defaultValue?: any;
  customPropId: string;
  description: string;
  onChange(value: any): void;
  onDelete(): void;
  attributeName: string;
  options?: any;
  isEnabled: boolean;
  disabledDescription?: string;
  disableDynamicData?: boolean;
  definition: CustomPropDefinition;
  context: Context | null;
  products: StoreProduct[];
  templateProduct: StoreProduct | null;
  placeholder?: string;
};

type ComponentPropTypeEditorData = {
  render(renderProps: CustomComponentPropRenderProps): React.ReactNode;
  shouldRender(component: Component, context?: Context): boolean;
  defaultValue: any;
  hasCustomWrapper?: boolean;
};

function MultilineFieldControl({
  value,
  description,
  onChange,
}: {
  value: string;
  description: string;
  onChange: (value: string) => void;
}) {
  const handleTextareaChange = useDebouncedCallback((value: string) => {
    onChange(value);
  }, 300);

  return (
    <FieldWithDescription description={description}>
      <DeprecatedTextarea
        size="sm"
        layoutClassName="w-full"
        value={value}
        onChange={handleTextareaChange}
      />
    </FieldWithDescription>
  );
}

export const getCustomComponentPropTypeEditorData = (
  pt: CustomComponentPropType,
): ComponentPropTypeEditorData => {
  return customComponentPropTypeToEditorData[pt]!;
};

const customComponentPropTypeToEditorData: Record<
  CustomComponentPropType,
  ComponentPropTypeEditorData | null
> = {
  component: {
    render: () => null,
    shouldRender: () => false,
    defaultValue: null,
  },
  timeInterval: {
    render: ({
      value,
      description,
      onChange,
      isEnabled,
      disabledDescription,
    }) => {
      const { minutes, hours, seconds, days } = value as {
        minutes: number;
        hours: number;
        seconds: number;
        days: number;
      };
      return (
        <FieldWithDescription
          description={
            isEnabled ? description : disabledDescription ?? description
          }
        >
          <DeprecatedLabeledControl label="Seconds" size="sm">
            <Stepper
              value={seconds}
              field="seconds"
              placeholder="0"
              onChange={(newSeconds) => {
                onChange({
                  ...value,
                  seconds: newSeconds,
                });
              }}
            />
          </DeprecatedLabeledControl>
          <DeprecatedLabeledControl label="Minutes" size="sm">
            <Stepper
              value={minutes}
              field="minutes"
              placeholder="0"
              onChange={(newMinutes) => {
                onChange({
                  ...value,
                  minutes: newMinutes,
                });
              }}
            />
          </DeprecatedLabeledControl>
          <DeprecatedLabeledControl label="Hours" size="sm">
            <Stepper
              value={hours}
              field="hours"
              placeholder="0"
              onChange={(newHours) => {
                onChange({
                  ...value,
                  hours: newHours,
                });
              }}
            />
          </DeprecatedLabeledControl>
          <DeprecatedLabeledControl label="Days" size="sm">
            <Stepper
              value={days}
              field="days"
              placeholder="0"
              onChange={(newDays) => {
                onChange({
                  ...value,
                  days: newDays,
                });
              }}
            />
          </DeprecatedLabeledControl>
        </FieldWithDescription>
      );
    },
    defaultValue: 0,
    shouldRender: () => true,
  },
  boolean: {
    render: ({
      value,
      description,
      onChange,
      isEnabled,
      disabledDescription,
      definition,
    }) => {
      let displayedValue = Boolean(value);
      if (!isEnabled && !definition.preserveValueWhenDisabled) {
        displayedValue = Boolean(definition.defaultValue);
      }

      return (
        <SwitchWithDescription
          disabled={!isEnabled}
          isOn={displayedValue}
          size="sm"
          onChange={onChange}
          label={isEnabled ? description : disabledDescription ?? description}
        />
      );
    },
    shouldRender: () => true,
    defaultValue: false,
  },
  product_variant: {
    render: ({ value, description, options, onChange }) => {
      const comboboxOptions = options.map((opt: ComboboxOption) => ({
        ...opt,
        value: String(opt.value),
      }));

      const selectedOption = comboboxOptions.find(
        (v: ComboboxOption) => v.value === String(value),
      );
      const selectedVariantLabel = selectedOption?.label as string | undefined;
      const isInvalid = value != null && !selectedOption?.isSelectable;

      return (
        <FieldWithDescription description={description}>
          <Combobox
            title="Select a variant"
            side="left"
            sideOffset={16}
            options={comboboxOptions}
            value={String(value)}
            onChange={(newSelectedVariantIdString) => {
              onChange(
                newSelectedVariantIdString
                  ? Number(newSelectedVariantIdString)
                  : null,
              );
            }}
            trigger={
              <Combobox.SelectionButton
                title={selectedVariantLabel ?? "Select a Variant"}
                titleAlignment="start"
                placeholder="Select a Variant"
                size="sm"
                validityState={isInvalid ? "invalid" : undefined}
                endEnhancer={
                  value != null ? (
                    <FormFieldXButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange(null);
                      }}
                    />
                  ) : undefined
                }
              />
            }
          />
        </FieldWithDescription>
      );
    },
    shouldRender: (component) => {
      return (
        Boolean(component.props._autoSelectVariant ?? true) &&
        Boolean((component.props._product as ProductRef)?.productId)
      );
    },
    defaultValue: null,
  },
  productVariants: {
    render: ({ value: selectedValues, description, context, onChange }) => {
      const variants: ReploShopifyVariant[] =
        context?.attributes?._variants ?? [];
      const baseOptions: ComboboxOption[] = variants.map(
        (variant: ReploShopifyVariant) => {
          return {
            label: variant.title,
            value: String(variant.id),
            isSelectable: true,
            size: "sm",
            toolTip: variant.title.length > 28 ? variant.title : null,
          };
        },
      );

      const SELECT_ALL_VALUE = "_all_";
      const allSelectableValues = baseOptions
        .filter((opt) => opt.isSelectable)
        .map((opt: ComboboxOption) => opt.value);

      const currentSelectedValues = (
        Array.isArray(selectedValues) ? selectedValues : []
      ).map(String);

      const allSelected =
        selectedValues === null ||
        (Array.isArray(selectedValues) &&
          allSelectableValues.every((val) => {
            return currentSelectedValues.includes(val);
          }));

      const comboboxOptions: ComboboxOption[] = [
        {
          label: "Select All",
          value: SELECT_ALL_VALUE,
          isSelectable: true,
        },
        ...baseOptions,
      ] as ComboboxOption[];

      const comboboxValues = allSelected
        ? [SELECT_ALL_VALUE, ...allSelectableValues]
        : currentSelectedValues;

      let triggerTitle: string;
      if (allSelected) {
        triggerTitle = "All Variants";
      } else if (currentSelectedValues.length === 0) {
        triggerTitle = "Select Variant";
      } else {
        triggerTitle = `${currentSelectedValues.length} Variant${
          currentSelectedValues.length !== 1 ? "s" : ""
        }`;
      }

      if (variants.length > 0) {
        return (
          <FieldWithDescription description={description}>
            <Combobox
              title="Select Variants"
              side="left"
              sideOffset={16}
              options={comboboxOptions}
              isMultiselect
              value={comboboxValues}
              onChange={(newValues: string[]) => {
                const allSelectedNow = newValues.includes(SELECT_ALL_VALUE);

                if (allSelectedNow && !allSelected) {
                  onChange(null);
                } else if (!allSelectedNow && allSelected) {
                  onChange([]);
                } else {
                  const actualSelectedValues = newValues.filter(
                    (v) => v !== SELECT_ALL_VALUE,
                  );
                  onChange(actualSelectedValues.map(Number));
                }
              }}
              trigger={
                <Combobox.SelectionButton
                  title={triggerTitle}
                  titleAlignment="start"
                  placeholder="Select Variant"
                  startEnhancer={
                    <Badge
                      type="icon"
                      icon={<PackagePlus size={10} className="text-sheet" />}
                      UNSAFE_className="bg-primary"
                    />
                  }
                  size="sm"
                  layoutClassName="w-full"
                />
              }
            />
          </FieldWithDescription>
        );
      }
    },
    shouldRender: () => true,
    defaultValue: null,
  },
  productSellingPlan: {
    shouldRender: (component) => {
      return isNotNullish(component.props._product);
    },
    render: ({
      value,
      description,
      onChange,
      context,
      component,
      products,
      templateProduct,
    }) => {
      const product = getProduct(component.props._product ?? null, context, {
        productMetafieldValues: {},
        variantMetafieldValues: {},
        products,
        currencyCode: DEFAULT_ACTIVE_CURRENCY,
        language: DEFAULT_ACTIVE_LANGUAGE,
        moneyFormat: DEFAULT_MONEY_FORMAT,
        moneyWithCurrencyFormat: DEFAULT_MONEY_WITH_CURRENCY_FORMAT,
        templateProduct,
        isEditor: true,
        isShopifyProductsLoading: false,
      });

      if (!product) {
        return null;
      }

      const allowAllPlans =
        !component.props._defaultSelectedVariantId ||
        !(
          isBoolean(component.props._autoSelectVariant) &&
          component.props._autoSelectVariant
        );

      return (
        <FieldWithDescription description={description}>
          <SellingPlanSelector
            product={product}
            allowAllPlans={allowAllPlans}
            selectedSellingPlanId={
              value as SelectedSellingPlanIdOrOneTimePurchase | null
            }
            onChange={onChange}
          />
        </FieldWithDescription>
      );
    },
    defaultValue: null,
  },
  date: {
    render: ({ value, description, onChange }) => {
      const timeFormat =
        value != null ? toDatetimeLocal(new Date(value)) : null;
      return (
        <div className="flex flex-col gap-2">
          <span className="text-muted w-4/5 text-xs">{description}</span>
          <input
            value={timeFormat ?? undefined}
            type="datetime-local"
            className="text-xs"
            onChange={(e) => {
              try {
                onChange(new Date(e.target.value).toISOString());
              } catch (error) {
                if (error instanceof ReferenceError) {
                  throw new InvalidDateValueError(e.target.value);
                }
                throw error;
              }
            }}
          />
        </div>
      );
    },
    shouldRender: () => true,
    defaultValue: false,
  },
  product: {
    render: ({ value, description, onChange }) => {
      return (
        <ProductsCustomPropModifier
          onChange={(value) => onChange(value)}
          products={value}
          allowDynamicData
          description={description}
          isMultiProducts={false}
        />
      );
    },
    defaultValue: false,
    shouldRender: () => {
      return true;
    },
    hasCustomWrapper: true,
  },
  products: {
    render: ({ value, onChange }) => {
      return (
        <ProductsCustomPropModifier
          isMultiProducts={true}
          onChange={(productRef) => onChange(productRef)}
          products={value}
          allowDynamicData={false}
        />
      );
    },
    defaultValue: false,
    shouldRender: () => true,
    hasCustomWrapper: true,
  },
  productsWithVariants: {
    render: () => null,
    shouldRender: () => false,
    defaultValue: null,
  },
  string: {
    render: ({ value, description, onChange, disableDynamicData }) => {
      return (
        <FieldWithDescription description={description}>
          <InputWithDynamicData
            onChange={onChange}
            value={value}
            disableDynamicData={disableDynamicData}
            dynamicDataTargetType="text"
          />
        </FieldWithDescription>
      );
    },
    defaultValue: "",
    shouldRender: () => true,
  },
  url: {
    render: ({ value, description, onChange, disableDynamicData }) => {
      return (
        <FieldWithDescription description={description}>
          <InputWithDynamicData
            onChange={onChange}
            value={value}
            disableDynamicData={disableDynamicData}
            dynamicDataTargetType="text/url"
          />
        </FieldWithDescription>
      );
    },
    defaultValue: "",
    shouldRender: () => true,
  },
  multiline: {
    render: ({ value, description, onChange }) => {
      return (
        <MultilineFieldControl
          value={value}
          description={description}
          onChange={onChange}
        />
      );
    },
    defaultValue: "",
    shouldRender: () => true,
  },
  float: {
    render: ({ value, description, onChange }) => {
      return (
        <FieldWithDescription description={description}>
          <Input
            value={value}
            onChange={(e) => {
              const float = Number.parseFloat(e.target.value);
              if (!Number.isNaN(float)) {
                onChange(e.target.value);
              }
            }}
          />
        </FieldWithDescription>
      );
    },
    defaultValue: 0,
    shouldRender: () => true,
  },
  integer: {
    render: ({
      value,
      description,
      onChange,
      isEnabled,
      disabledDescription,
      definition,
    }) => {
      return (
        <FieldWithDescription
          description={
            isEnabled ? description : disabledDescription ?? description
          }
        >
          <IntegerSelector
            min={definition.min}
            value={Number.parseInt(String(value))}
            onChange={(newQuantity) => {
              onChange(newQuantity);
            }}
          />
        </FieldWithDescription>
      );
    },
    defaultValue: 0,
    shouldRender: () => true,
  },
  color: {
    render: ({ value, description, onChange }) => {
      return (
        <FieldWithDescription description={description}>
          <SolidColorSelector
            value={value}
            onChange={onChange}
            popoverSideOffset={14}
            // Note (Noah, 2025-05-22): Don't show saved styles in custom component props,
            // because they're not properly able to be translated into styles for liquid sections
            showSavedStyles={false}
            onSelectSavedStyle={onChange}
          />
        </FieldWithDescription>
      );
    },
    defaultValue: 0,
    shouldRender: () => true,
  },
  ratingBalancer: {
    render: ({ value, description, onChange }) => {
      const defaultValue = 0;
      const onSubmit = () => {
        if (!value) {
          onChange(defaultValue);
          return;
        }
        const balancedValue = Math.ceil(value * 2) / 2;
        onChange(balancedValue);
      };
      return (
        <FieldWithDescription description={description}>
          <Input
            value={value}
            onBlur={onSubmit}
            onEnter={onSubmit}
            onChange={(e) => {
              const float = Number.parseFloat(e.target.value || "0");
              if (!Number.isNaN(float)) {
                onChange(e.target.value);
              }
            }}
          />
        </FieldWithDescription>
      );
    },
    defaultValue: 0,
    shouldRender: () => true,
  },
  pixels: {
    render: ({ value, description, onChange, defaultValue }) => {
      return (
        <FieldWithDescription description={description}>
          <LengthInputSelector
            value={value}
            minDragValues={{ all: 0 }}
            placeholder={defaultValue ?? "16px"}
            field="customProp"
            onChange={(value) => onChange(value)}
            allowsNegativeValue={false}
            resetValue={defaultValue ?? "16px"}
            anchorValue={defaultValue ?? "16px"}
            inputClassName="grow"
          />
        </FieldWithDescription>
      );
    },
    defaultValue: 0,
    shouldRender: () => true,
  },
  pixelsIncludingNegativeValues: {
    render: ({ value, description, onChange, defaultValue }) => (
      <FieldWithDescription description={description}>
        <LengthInputSelector
          value={value}
          placeholder={defaultValue}
          field="customProp"
          onChange={(value) => onChange(value)}
          resetValue={defaultValue}
          anchorValue={defaultValue}
        />
      </FieldWithDescription>
    ),
    defaultValue: 0,
    shouldRender: () => true,
  },
  hashmark: {
    render: ({ value, description, onChange }) => {
      return (
        <FieldWithDescription description={description}>
          <div className="flex flex-row items-center">
            <div className="flex-1">
              <HashmarkInput
                value={String(value ?? "")}
                onValueChange={onChange}
              />
            </div>
          </div>
        </FieldWithDescription>
      );
    },
    defaultValue: "",
    shouldRender: () => true,
  },
  location: null,
  collection: null,
  image: {
    render: ({ value, customPropId, description, definition }) => {
      return (
        <div className="flex flex-col justify-between gap-2">
          <span className="text-muted text-xs">{description}</span>
          <ImageCustomPropModifier
            value={value}
            customPropId={customPropId}
            definition={definition}
          />
        </div>
      );
    },
    shouldRender: () => true,
    defaultValue: false,
  },
  inlineItems: {
    render: ({ value, description, onChange }) => {
      return (
        <FieldWithDescription description={description}>
          <ItemsSelector
            allowedItemTypes={["inline"]}
            value={value}
            onChange={(value) => {
              onChange(value);
            }}
          />
        </FieldWithDescription>
      );
    },
    shouldRender: () => true,
    defaultValue: null,
  },
  htmlAttributes: {
    render: ({ value, description, onChange, onDelete }) => {
      return (
        <FieldWithDescription description={description}>
          <ItemsSelector
            allowedItemTypes={["htmlAttributes"]}
            value={value}
            onChange={onChange}
            onDelete={onDelete}
          />
        </FieldWithDescription>
      );
    },
    shouldRender: () => true,
    defaultValue: null,
  },
  dynamicItems: {
    render: ({ value, description, onChange, onDelete }) => {
      return (
        <FieldWithDescription description={description}>
          <CombinedItemsSelector
            value={value}
            onChange={onChange}
            onDelete={() => onDelete()}
          />
        </FieldWithDescription>
      );
    },
    shouldRender: () => true,
    defaultValue: null,
  },
  anyDynamicImage: {
    render: ({
      value,
      onChange,
      onDelete,
      component,
    }: Omit<CustomComponentPropRenderProps, "value"> & {
      value: string | null;
    }) => {
      return (
        <DynamicDataSelector
          side="left"
          targetType="image"
          initialPath={getPathFromVariable(value ?? "")}
          onChange={onChange}
          triggerClassName="w-full"
          trigger={
            <DynamicDataValueIndicator
              type="text"
              labelClassname="text-left"
              templateValue={value}
              componentId={component.id}
              onRemove={onDelete}
            />
          }
        ></DynamicDataSelector>
      );
    },
    shouldRender: () => true,
    defaultValue: null,
  },
  seconds: {
    render: ({ value, description, onChange, attributeName }) => {
      const onChangeValue = (
        v: any,
        type: "increment" | "decrement" | "input",
      ) => {
        let val = Number.parseFloat(v);
        if (type === "increment") {
          val = val + 1;
        } else if (type === "decrement") {
          val = Math.max(val - 1, 0);
        }

        if (Number.isNaN(val)) {
          val = 0;
        }

        onChange(`${val}s`);
      };
      return (
        <FieldWithDescription description={description}>
          <div className="flex flex-row items-end justify-between">
            <div className="mr-1 flex-1">
              <LengthInputSelector
                placeholder="0s"
                key={`props.${attributeName}`}
                field={`props.${attributeName}`}
                value={value}
                onChange={(v: any) => onChange(v)}
                draggingType="vertical"
                metrics={["s"]}
                resetValue="0s"
                anchorValue="0s"
                minDragValues={{ all: 0 }}
                minValues={{ all: 0 }}
                allowsNegativeValue={false}
              />
            </div>
            <ButtonGroupComponent
              options={[
                {
                  label: <Minus size={12} />,
                  onClick: () => onChangeValue(value, "decrement"),
                  isDisabled: value > 0,
                },
                {
                  label: <Plus size={12} />,
                  onClick: () => onChangeValue(value, "increment"),
                },
              ]}
            />
          </div>
        </FieldWithDescription>
      );
    },
    shouldRender: () => true,
    defaultValue: null,
  },
  productOptions: {
    render: ({ value, onChange }) => {
      return <OptionsCustomPropModifier value={value} onChange={onChange} />;
    },
    shouldRender: () => true,
    defaultValue: null,
  },
  liquidCodeEditor: {
    render: ({ value, description, onChange }) => {
      return (
        <FieldWithDescription description={description}>
          <CodeEditorCustomPropModifier
            language="liquid"
            value={value}
            onChange={(value) => onChange(value)}
          />
        </FieldWithDescription>
      );
    },
    shouldRender: () => true,
    defaultValue: null,
  },
  htmlCodeEditor: {
    render: ({ value, description, onChange }) => {
      return (
        <FieldWithDescription description={description}>
          <CodeEditorCustomPropModifier
            language="html"
            value={value}
            onChange={(value) => onChange(value)}
          />
        </FieldWithDescription>
      );
    },
    shouldRender: () => true,
    defaultValue: null,
  },
  cssCodeEditor: {
    render: ({ value, description, onChange }) => {
      return (
        <FieldWithDescription description={description}>
          <CodeEditorCustomPropModifier
            language="css"
            value={value}
            onChange={(value) => onChange(value)}
          />
        </FieldWithDescription>
      );
    },
    shouldRender: () => true,
    defaultValue: null,
  },

  shopifyThemeSections: {
    render: ({ value, description, onChange }) => {
      return (
        <FieldWithDescription description={description}>
          <SectionsSelectable defaultValue={value} onChange={onChange} />
        </FieldWithDescription>
      );
    },
    shouldRender: () => true,
    defaultValue: null,
  },

  selectable: {
    render: ({ value, description, onChange, options = [], placeholder }) => {
      return (
        <FieldWithDescription description={description}>
          <Selectable
            options={options}
            onSelect={(value) => onChange(value)}
            value={value}
            placeholder={placeholder ?? "Select an option"}
          />
        </FieldWithDescription>
      );
    },
    shouldRender: () => true,
    defaultValue: null,
  },
  selectableButtons: {
    render: () => null,
    shouldRender: () => false,
    defaultValue: null,
  },
  swatches: {
    render: () => {
      return <SwatchesCustomPropModifier key="swatches" />;
    },
    shouldRender: () => true,
    defaultValue: null,
    hasCustomWrapper: true,
  },
  percentage: {
    render: ({ value, description, onChange, attributeName }) => {
      return (
        <FieldWithDescription description={description}>
          <LengthInputSelector
            value={value}
            metrics={["%"]}
            minValues={{ "%": 0 }}
            maxValues={{ "%": 100 }}
            placeholder="50%"
            field={`props.${attributeName}`}
            onChange={(value) => onChange(value)}
            allowsNegativeValue={false}
            resetValue="50%"
            anchorValue="50%"
            menuOptions={[
              {
                label: "0%",
                value: "0%",
              },
              {
                label: "25%",
                value: "25%",
              },
              {
                label: "50%",
                value: "50%",
              },
              {
                label: "75%",
                value: "75%",
              },
              {
                label: "100%",
                value: "100%",
              },
            ]}
          />
        </FieldWithDescription>
      );
    },
    defaultValue: "50%",
    shouldRender: () => true,
  },
  productSellingPlans: {
    render: ({ value, description, context, onChange }) => {
      const sellingPlans = context?.attributes?._sellingPlans;

      const sortedSellingPlans = orderBy(
        sellingPlans ?? [],
        [(plan) => Boolean(plan.appId)],
        ["desc"],
      );

      const baseOptions: ComboboxOption[] =
        sortedSellingPlans?.map(
          (sellingPlan: ShopifySellingPlan): ComboboxOption => {
            const appName = sellingPlan.appName;
            return {
              label: sellingPlan.name,
              value: String(sellingPlan.id),
              isSelectable: true,
              groupTitle: appName ?? undefined,
            };
          },
        ) ?? [];

      const allSelectableValues = baseOptions
        .filter((opt: ComboboxOption) => opt.isSelectable)
        .map((opt: ComboboxOption) => opt.value);

      const currentSelectedValuesAsString = (
        Array.isArray(value) ? value : []
      ).map(String);

      const allSelected =
        value === null ||
        (Array.isArray(value) &&
          allSelectableValues.length > 0 &&
          allSelectableValues.every((val) => {
            return currentSelectedValuesAsString.includes(val as string);
          }));

      const comboboxOptions: ComboboxOption[] = [
        {
          label: "Select All",
          value: "_all_",
          isSelectable: true,
        },
        ...baseOptions,
      ] as ComboboxOption[];

      const comboboxValues = allSelected
        ? ["_all_", ...allSelectableValues]
        : currentSelectedValuesAsString;

      let triggerTitle: string;
      if (allSelected) {
        triggerTitle = "All Selling Plans";
      } else if (currentSelectedValuesAsString.length === 0) {
        triggerTitle = "Select Selling Plan";
      } else {
        triggerTitle = `${currentSelectedValuesAsString.length} Selling Plan${
          currentSelectedValuesAsString.length !== 1 ? "s" : ""
        }`;
      }

      if (baseOptions.length > 0) {
        return (
          <FieldWithDescription description={description}>
            <Combobox
              title="Select Selling Plans"
              side="left"
              sideOffset={16}
              options={comboboxOptions}
              isMultiselect
              value={comboboxValues}
              onChange={(newValues: string[]) => {
                const allSelectedNow = newValues.includes("_all_");

                if (allSelectedNow && !allSelected) {
                  onChange(null);
                } else if (!allSelectedNow && allSelected) {
                  onChange([]);
                } else {
                  const actualSelectedValues = newValues.filter(
                    (v) => v !== "_all_",
                  );
                  // Note (Jackson, 2025-04-01): ree
                  onChange(actualSelectedValues.map(Number));
                }
              }}
              trigger={
                <Combobox.SelectionButton
                  title={triggerTitle}
                  titleAlignment="start"
                  placeholder="Select Selling Plan"
                  startEnhancer={
                    <Badge
                      type="icon"
                      icon={<PackagePlus size={10} className="text-sheet" />}
                      UNSAFE_className="bg-primary"
                    />
                  }
                  size="sm"
                  layoutClassName="w-full"
                />
              }
            />
          </FieldWithDescription>
        );
      }
    },
    shouldRender: () => true,
    defaultValue: null,
  },
  bundleDiscountThresholds: {
    render: () => null,
    shouldRender: () => false,
    defaultValue: null,
  },
};

function HashmarkInput({
  value,
  onValueChange,
}: {
  value: string;
  onValueChange(value: string): void;
}) {
  const inputProps = useOverridableInput({ value, onValueChange });
  return (
    <Input
      startEnhancer={<span className="text-muted">#</span>}
      {...inputProps}
    />
  );
}

const InputWithDynamicData = ({
  onChange,
  value,
  disableDynamicData = false,
  dynamicDataTargetType,
}: {
  onChange: (value: string) => void;
  value: string;
  disableDynamicData?: boolean;
  dynamicDataTargetType: DynamicDataTargetType;
}) => {
  const inputProps = useOverridableInput({ value, onValueChange: onChange });
  return typeof value === "string" &&
    isDynamicDataValue(value) &&
    !disableDynamicData ? (
    <DynamicDataSelector
      targetType={dynamicDataTargetType}
      initialPath={value.split(".")}
      onChange={(value) => onChange(value as string)}
      trigger={
        <DynamicDataValueIndicator
          type="text"
          templateValue={value}
          onRemove={() => {
            onChange("");
          }}
        />
      }
    />
  ) : (
    <div className="flex flex-row items-center gap-2">
      <Input layoutClassName="flex-1" {...inputProps} />
      {!disableDynamicData && (
        <DynamicDataSelector
          targetType={dynamicDataTargetType}
          onChange={(value) => onChange(value as string)}
          trigger={<DynamicDataButton />}
        />
      )}
    </div>
  );
};

function SectionsSelectable({
  onChange,
  defaultValue,
}: {
  onChange: (value: string | null) => void;
  defaultValue: string | null;
}) {
  const themeId = useEditorSelector(selectThemeId);
  const { project } = useCurrentProjectContext();
  const shopifyUrl = useEditorSelector(selectStoreShopifyUrl);
  const { isSuccess, isLoading, data } =
    assetRouter.getShopifyThemeSections.useQuery(
      project?.id && shopifyUrl
        ? {
            storeId: project.id,
            themeId,
          }
        : skipToken,
    );

  if (project && !shopifyUrl) {
    return (
      <ConnectShopifyCallout
        cookieValue={{
          type: "sections",
          projectId: project.id,
          workspaceId: project.ownerWorkspaceId,
        }}
      />
    );
  }

  const comboboxOptions = isSuccess
    ? data.sections.map((section) => {
        const sectionName = getSectionName(section.key);
        return {
          label: sectionName,
          value: sectionName,
          isSelectable: true,
        };
      })
    : [];

  const selectedOption = comboboxOptions.find(
    (opt: ComboboxOption) => opt.value === defaultValue,
  );

  return (
    <Combobox
      title="Select Shopify Sections"
      areOptionsSearchable
      side="left"
      sideOffset={16}
      options={comboboxOptions}
      value={defaultValue ?? undefined}
      onChange={(newValue) => onChange(newValue ?? null)}
      trigger={
        <Combobox.SelectionButton
          title={selectedOption?.label ?? "Select Section"}
          titleAlignment="start"
          placeholder={isLoading ? "Loading Sections…" : "Select Section"}
          size="sm"
          layoutClassName="w-full"
          endEnhancer={
            defaultValue != null ? (
              <FormFieldXButton
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                }}
              />
            ) : undefined
          }
        />
      }
    />
  );
}

function getSectionName(key: string) {
  return key.replace(/^sections\//, "").replace(/\.liquid$/, "");
}
